import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import useSWR from 'swr';
import { ADULTS_DEFAULT, URLParameters } from '@/app/common/urlParameters';
import getSearchParams from '../../search/common/getSearchParams';
import { useInitialParams } from '../InitialParamsProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { fetchWithTracingAndLogging } from '../common/fetchWithTracing';
import { BASE_PATH } from '../../search/utils/path';
import { getLoggingInfo } from '../lib/loggingInfo';
import type { DateRange } from '../common/dates';
import { isValidHotelId } from '../common/common.helper.ts';

export function usePriceCalendar(priceCalendarDates: DateRange | undefined) {
  const { pageType, internalHotelId } = useInitialParams();
  const { accommodationOnly } = useUrlSearchParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const searchParams = getSearchParams();
  const nights = priceCalendarDates?.nights;

  return useSWR<PriceCalendarResponse | null>(
    priceCalendarDates?.from && priceCalendarDates?.to && nights && isValidHotelId(internalHotelId)
      ? getPriceCalendarKey(priceCalendarDates.from, priceCalendarDates.to, nights)
      : undefined,
    async () => {
      const payloadData: PriceCalendarPayload = {
        accommodationOnly,
        currency: localization.currency,
        from: priceCalendarDates?.from || '',
        internalHotelId,
        locale: localization.locale,
        logged: commonData.authResponse.loggedIn,
        nights: (nights || 2).toString(),
        adults: searchParams[URLParameters.ADULTS] || ADULTS_DEFAULT.toString(),
        childrenAges: searchParams[URLParameters.AGES] || '',
        page: pageType,
        to: priceCalendarDates?.to || '',
        origin: searchParams[URLParameters.ORIGIN] || '',
        taxesIncluded: localization.country === 'GB' && accommodationOnly,
      };
      return fetchWithTracingAndLogging<PriceCalendarResponse>(
        `${BASE_PATH}/api/hotel-detail/price-calendar`,
        {
          headers: { 'content-type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(payloadData),
        },
        getLoggingInfo(pageType, 'priceCalendarHotel', 'priceCalendarApiRoute'),
        searchInfoData.searchTraceParent,
        commonData
      );
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
    }
  );
}

export function getPriceCalendarKey(from: string, to: string, nights: number) {
  return `price-calendar-${from}-${to}-${nights}`;
}
